/**
 * Credit-balance store (P1-T11).
 *
 * - Fetched on app start (after auth) and whenever the tab becomes visible.
 * - `creditsChanged` lets any screen refresh the top-bar pill.
 * - `refreshCredits()` is the hook P1-T15 (generation) and P1-T17 (paywall)
 *   call after a spend or purchase so the balance updates live.
 */
import { api } from '../api/client.js';
import { hasAccount } from './auth.js';
import { Emitter } from './events.js';

/** Fired after every successful balance fetch (or when cleared on logout). */
export const creditsChanged = new Emitter();

let balance: number | null = null;
let inflight: Promise<number | null> | null = null;

export function getBalance(): number | null {
  return balance;
}

/** Clear local balance state (call on sign-out). */
export function clearBalance(): void {
  balance = null;
  creditsChanged.emit();
}

/**
 * Fetch the balance from the server. Concurrent callers share one request.
 * Resolves to null when signed out or when the request fails (the top bar
 * then shows "—" and a tap retries).
 */
export function refreshCredits(): Promise<number | null> {
  // Anonymous guest sessions have no wallet: treat the balance as unknown so
  // tools offer "Generate" (which asks them to sign in) rather than "Get credits".
  if (!hasAccount()) {
    clearBalance();
    return Promise.resolve(null);
  }
  if (inflight) return inflight;
  const attempt: Promise<number | null> = api.getBalance().then(
    ({ balance: b }) => {
      balance = b;
      creditsChanged.emit();
      return b;
    },
    () => null,
  );
  inflight = attempt;
  void attempt.then(() => {
    if (inflight === attempt) inflight = null;
  });
  return attempt;
}
